import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { IconTile } from '../../shared/components/IconTile';
import { Pill } from '../../shared/components/Pill';
import type { SemanticColor } from '../../shared/components/semanticColor';
import type { ProjectType } from '../../core/types/data-models';
import { PROJECT_TYPE_PROFILES } from './projectTypeProfiles';
import { ONBOARDING_PATH, findOnboardingRoute, onboardingRouteFor } from './onboardingRoutes';

const CARD_STYLE: Record<ProjectType, { icon: ReactNode; color: SemanticColor }> = {
  IDEA_VALIDATION: { icon: <LightbulbOutlinedIcon fontSize="inherit" />, color: 'primary' },
  BUSINESS_VALIDATION: { icon: <QueryStatsIcon fontSize="inherit" />, color: 'cyan' },
  STOCK_VALIDATION: { icon: <ShowChartIcon fontSize="inherit" />, color: 'success' },
  STRATEGY_MANAGEMENT: { icon: <InsightsOutlinedIcon fontSize="inherit" />, color: 'secondary' },
};

/**
 * `/onboarding` — "What brings you to Vibelin?": one card per project type
 * (name, tagline, audience from its profile), each opening its wizard. Types
 * whose wizard is not built yet are shown but not clickable.
 */
export function OnboardingPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
        <Typography variant="h4" component="h1" fontWeight={800} letterSpacing="-0.02em">
          What brings you to Vibelin?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Pick the path that fits your situation. Each one creates a project and starts the right analysis for it.
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {PROJECT_TYPE_PROFILES.map((profile) => {
          const route = onboardingRouteFor(profile.type);
          const available = Boolean(route?.Wizard);
          const { icon, color } = CARD_STYLE[profile.type];
          return (
            <GlassPanel
              key={profile.type}
              component="li"
              sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, opacity: available ? 1 : 0.75 }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <IconTile icon={icon} color={color} size="lg" />
                {!available && <Pill mono={false}>Coming soon</Pill>}
              </Stack>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h2" fontWeight={700} lineHeight={1.25}>
                  {profile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {profile.tagline}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  For {profile.audience}.
                </Typography>
              </Box>
              <Button
                variant={available ? 'contained' : 'outlined'}
                endIcon={<ArrowForwardIcon />}
                disabled={!available}
                onClick={() => route && navigate(`${ONBOARDING_PATH}/${route.slug}`)}
              >
                Get started
              </Button>
            </GlassPanel>
          );
        })}
      </Box>
    </Box>
  );
}

/** `/onboarding/:projectType` — that type's wizard; an unknown or not-yet-built slug falls back to the picker. */
export function OnboardingWizardPage() {
  const { projectType: slug } = useParams();
  const route = findOnboardingRoute(slug);
  if (!route?.Wizard) return <Navigate to={ONBOARDING_PATH} replace />;
  const Wizard = route.Wizard;
  return <Wizard />;
}
